import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import * as storeNgrx from '@ngrx/store';
import {Select, Store} from '@ngxs/store';
import {Observable, of, throwError, zip} from 'rxjs';
import {catchError, debounceTime, delay, finalize, first, map, switchMap, tap} from 'rxjs/operators';
import {AppState} from 'src/app/app.reducer';
import {saveSubscriptionOutOfStockBraze} from 'src/app/data/actions/subscription-out-of-stock.action';
import {MobileConnectorService} from 'src/app/data/networking/services/shared/mobile-connector/mobile-connector.service';
import {countryConfig, DefaultAnonymousData} from 'src/country-config/country-config';
import {UpdateSessionModalEvent} from 'src/app/ui/shared/event-listeners/update-session-modal/update-session-modal.event';
import {IpRes} from 'src/app/data/networking/models/customer/responses/ip-res';
import {Customer, iDataValidateCode} from 'src/app/data/networking/models/customer/customer';
import {SaveNonStockItem} from 'src/app/data/networking/models/customer/requests/save-non-stock-item';
import {VerifyCustomerRes} from 'src/app/data/networking/models/customer/responses/verify-customer-res';
import {SaveNonStockItemRes} from 'src/app/data/networking/models/customer/responses/save-non-stock-item-res';
import {SessionEvent} from '../../../../ui/shared/event-listeners/session/session.event';
import {SaveCustomer} from '../../../actions/customer.actions';
import {SasCartManager} from '../../../manager-v3/sas-subscription/sas-cart/sas-cart.manager';
import {Login} from '../../../networking/models/customer/requests/login';
import {Product} from '../../../networking/models/product/responses/product.class';
import {CustomerService} from '../../../networking/services/customer/customer.service';
import {GoogleTagService} from '../../../networking/services/shared/google-tag/google-tag.service';
import {BrazeManager} from '../../braze/braze.manager';
import {CartManager} from '../../cart/cart.manager';
import {ConfigurationManager} from '../../configuration/configuration.manager';
import {LocalStorageManager} from '../../local-storage/local-storage.manager';
import {SessionStorageManager} from '../../session-storage/session-storage.manager';
import {IpService} from '../../../networking/services/customer/ip.service';
import {LocationManager} from '../../location/location.manager';
import {BatchManager} from '../../batch/batch.manager';
import {ToastEvent} from 'src/app/ui/shared/event-listeners/toast/toast.event';
import {TokenJwtResponse} from 'src/app/data/networking/models/shared/responses/token-jwt.response';
import {CreatedCustomerRes, iResponseVerifyUser} from 'src/app/data/networking/models/customer/responses/customer-res';
import {LoginBmcResponse} from 'src/app/data/networking/models/customer/responses/login-res';
import { UpdateCustomerEvent } from 'src/app/ui/shared/event-listeners/update-customer/update-customer.event';
import { UserOrigin } from 'src/app/data/networking/models/customer/requests/get-user-origin';


@Injectable({
  providedIn: 'root',
})
export class CustomerManager {
  count = 0;
  codeSmsChangePass = null;
  activeModalCreatePass = false;
  keyClient = {keyClient: CustomerManager.getKeyClient()};
  @Select(state => state.customerStore) customerStore$: Observable<any>;

  get defaultAnonymousData () : DefaultAnonymousData {
    return countryConfig.defaultAnonymousData;
  }

  static getKeyClient() {
    return countryConfig.generalEnv.keyClient;
  }

  static getCoordinates() {
    return {
      lon: countryConfig.generalEnv.coordinates.lon,
      lat: countryConfig.generalEnv.coordinates.lat,
    };
  }

  constructor(private lsm: LocalStorageManager,
              private customerService: CustomerService,
              private ssm: SessionStorageManager,
              private store: Store,
              private storeNgrx: storeNgrx.Store<AppState>,
              private sessionEvent: SessionEvent,
              private router: Router,
              private gtmService: GoogleTagService,
              private sasCartManager: SasCartManager,
              private mcService: MobileConnectorService,
              private configurationManager: ConfigurationManager,
              private cartManager: CartManager,
              private sessionModalStatusEvent: UpdateSessionModalEvent,
              private braze: BrazeManager,
              private ipService: IpService,
              private locationManager: LocationManager,
              private batchManager: BatchManager,
              private toastEvent: ToastEvent,
              private updateCustomerEvent: UpdateCustomerEvent,
            ) { }

  saveCustomer(customer) {
    this.store.dispatch(new SaveCustomer(customer)).subscribe(saveCustomer => {
      this.setLocalCustomer(saveCustomer?.customerStore?.customer);
      const isAnonymous = !customer.customerId;
      this.sessionEvent.updateHasCustomer({status: true, customer, isAnonymous});
      if (!isAnonymous) {
        this.sessionEvent.changeLoginStatus(true);
      }
    });
    this.logPlatformOpenedEvent();
  }

  logPlatformOpenedEvent() {
    if (this.customer?.analyticsUUID) {
      this.batchManager.logEvent('platform_opened', {
        source: 'direct',
        path: window.location.pathname,
      }).pipe(catchError(() => null)).subscribe();
    }
  }

  hasCustomer(): Observable<any> {
    if (this.customer && !this.customer.isAnonymous) {
        this.sessionEvent.updateHasCustomer({status: true, customer: this.customer, isAnonymous: this.customer.isAnonymous});
    }
    return this.sessionEvent.hasCustomer;
  }

  hasValidCustomer(): boolean {
    return Boolean(this.customer?.customerId) || !this.customer?.isAnonymous;
  }

  getUserOrigin(data: UserOrigin): Observable<iResponseVerifyUser> {
    return this.customerService.getUserOrigin(data);
  }

  getCustomerOrigin(data): Observable<any> {
    const basicParams = {
      email: data.email || data.socialEmail,
    };
    this.removePotencialCustomer();
    return this.customerService.getUserOrigin(basicParams);
  }

  get(params, customer: any = {}): Observable<any> {
    return this.customerStore$
      .pipe(
        debounceTime(500),
        switchMap(() => {
          let customerData = customer;
          const paramsData = params;

          if (!customer?.id) {
            customerData = this.customer && this.customer.id > 0 ? this.customer : null;
          }
          if (customerData && this.count < 2) {
            this.count += 1;
            paramsData.latitude = CustomerManager.getCoordinates().lat;
            paramsData.longitude = CustomerManager.getCoordinates().lon;
            paramsData.idCustomerWebSafe = customerData.idCustomerWebSafe;
            return this.customerService.get(paramsData).pipe(
              switchMap(res => this.getOrigin('ADDED_FROM_ALREADY_BOUGHT').pipe(
                map(origin => {
                  if (res?.previousItems) {
                    res.previousItems = res.previousItems.map(i => {
                      let iData = i;
                      iData = new Product(i);
                      iData.rsList = origin || 'Últimas compras';
                      iData.origin = origin;
                      return iData;
                    });
                  }
                  return res;
                }),
              )),
              tap(res => {

                const analyticsUUID = this.customer.analyticsUUID;
                const primeSubscription = this.ssm.get('primeSubscription');

                if (analyticsUUID) {
                  this.braze.getAllData(analyticsUUID).subscribe( dataBraze => {
                    if (dataBraze?.users) {
                      let listItems = dataBraze?.users[0].custom_attributes.NonStockItems;
                     if(listItems !== undefined){
                       this.storeNgrx.dispatch( saveSubscriptionOutOfStockBraze({items: listItems}));
                     }
                      const newDataBraze = {...res, ...dataBraze.users[0]};
                      if (dataBraze?.users[0]?.dob) {
                        const dateAux = new Date(dataBraze?.users[0]?.dob).toISOString().split('T')[0].split('-')
                        const birthday = `${dateAux[2]}/${dateAux[1]}/${dateAux[0]}`;
                        newDataBraze.birthday = birthday;
                      }
                      this.gtmService.getCustomer(newDataBraze, primeSubscription);
                    }
                  });
                } else {
                  this.gtmService.getCustomer(res, primeSubscription);
                }
                const newCustmerData = this.transformLocalData(res);
                this.saveCustomer({ ...customerData, ...newCustmerData, isAnonymous: !res?.customerId });
              }),
            );
          }
          return of(null);
        }),
      );
  }

  get customer() {
    const customer = this.lsm.get('customer');
    return customer || { isAnonymous: true} ;
  }

  validateToken(token, id): Observable<any> {
    const data: iDataValidateCode = {
      customerRef: id,
      code: token,
    };

    if (this.activeModalCreatePass) {
      this.codeSmsChangePass = token;
    }

    return this.customerService.validateToken(data).pipe(tap(dataUser => {
      this.ssm.set('dataTemporal', {...dataUser, customerRef: id});
      this.ssm.set('temporalCodeChangePass', data);
    }));
  }

  validateSmsToken(phone, call?, whatsapp?, register = true): Observable<any> {
    const callValue = Boolean(call);
    const whatsappValue = Boolean(whatsapp);
    const phoneValue = !callValue && !whatsappValue;

    const data = {
      phoneNumber: phone,
      bySMS: phoneValue,
      byWhatsApp: whatsappValue,
      byCall: callValue,
    };
    const params = {
      ...this.keyClient,
    };

    return this.customerService.validateSmsToken(data, params);
  }

  validateCodeToken(phone, code): Observable<any> {
    const data = {
      code,
      phoneNumber: phone,
    }
    return this.customerService.validateCodeToken(data);
  }

  validateSmsTokenFinal(): Observable<any> {
    return this.customerService.validateSmsTokenFinal(this.keyClient);
  }

  validateWhatsapp(data): Observable<any> {
    return this.customerService.validateWhatsapp(data);
  }

  validateCustomer(params: any): Observable<any> {
    const phone = (!!params?.phoneCode && !params?.phone.includes(params?.phoneCode))
      ? `${params.countryCode}${params.phoneCode}${params.phone}`
      : `${params.countryCode}${params.phone}`;

    const validationParams: any = {
      email: params.email,
      documentNumber: params.documentNumber,
      phone: phone,
      isEdit: params.isEdit ? params.isEdit : false,
      keyClient: CustomerManager.getKeyClient(),
    };
    if (params.documentTypeId) {
      validationParams.documentTypeId = params.documentTypeId;
    }
    return this.customerService.validateCustomer(validationParams);
  }

  simpleCreate(data?, isAnonymous = false): Observable<any> {
    let dataInfo = data || '';
    const params = {
      anonymous: isAnonymous,
      keyClient: CustomerManager.getKeyClient(),
      latitude: CustomerManager.getCoordinates().lat,
      longitude: CustomerManager.getCoordinates().lon,
    };

    if (isAnonymous) {
      dataInfo = {
        dataManagement: true,
        termsAndConditions: true,
      };
    } else {
      dataInfo.dataManagement = true;
      dataInfo.termsAndConditions = true;
    }

    return this.customerService.create(dataInfo, params)
      .pipe(
        switchMap(createResponseData => this.getCustomerCreateData(createResponseData)),
        tap(response => {
        if (response && (!response.error && !isAnonymous)) {
          this.saveCustomer(response);
          this.gtmService.registeredOk(data);

          const coord = CustomerManager.getCoordinates();

          const loginData: Login = {
            emailAddress: dataInfo.email,
            keyClient: CustomerManager.getKeyClient(),
            latitude: coord.lat,
            longitude: coord.lon,
          };

          const {provider} = dataInfo;
          const {isStore} = dataInfo;
          delete dataInfo.provider;
          this.gtmService.onSignUp({...data, ...response, provider, isStore, password: undefined});

          if (provider === 'EMAIL') {
            loginData.password = dataInfo.password;
          } else if (provider === 'GOOGLE') {
            loginData.password = dataInfo.tokenGoogle;
          } else {
            loginData.password = dataInfo.tokenFacebook;
          }

          if(this.mcService.isMobile) {
            this.mcService.logSignUp(provider);
          } else {
            this.gtmService.registeredOk(dataInfo);
          }
        }
      }));
  }
  create(data?, isAnonymous = false, formComplete = true): Observable<any> {
    let customerData = null;
    if (data?.phoneCode && !data?.phoneCode.includes(data?.phoneCode)) {
      data.phone = data.phoneCode + data.phone;
    }
    if (data) {
      customerData = {
        firstName: data.firstName,
        lastName: data.lastName,
        gender: data.gender[0].toUpperCase(),
        documentNumber: data.documentNumber,
        documentTypeId: data.documentTypeId,
        email: data.email,
        phone: data.countryCode + data.phone,
        provider: data.registeredBy,
        isStore: data.isStore,
      };

      if (customerData.provider === 'GOOGLE') {
        customerData.tokenGoogle = data.passwords.password;
      } else if (customerData.provider === 'FACEBOOK') {
        customerData.tokenFacebook = data.passwords.password;
      } else if (customerData.provider === 'APPLE') {
        customerData.uidFirebase = data.passwords.password;
      } else {
        customerData.password = data.passwords.password;
      }
    }

    return this.simpleCreate(customerData, isAnonymous)
      .pipe(tap(customer => {
        const customerSimple = customer;
        if (customerSimple) {
          if (customer?.documentNumber) {
            customer.isAnonymous = false;
            customerSimple.isAnonymous = false;
            isAnonymous = false;
          }
          if ( formComplete ) {
            this.saveCustomer(customerSimple);
          }
          if (isAnonymous) {
            customerSimple.id = 0;
            customerSimple.isAnonymous = true;
            this.sessionEvent.updateHasCustomer({status: true, customer: customerSimple, isAnonymous: true});
          }
        }
      }));
  }

  changePassword(rawData): Observable<any> {
    const data = {
      id: this.customer.id,
      oldPassword: rawData.oldPassword,
      newPassword: rawData.passwords.password
    };
    return this.customerService.changePassword(data);
  }

  getIpAddress(): Observable<IpRes> {
    return this.ipService.getIpAddress();
  }

  resetPassword(data): Observable<any> {
    const socialNetworks = ['gmail', 'facebook'];
    return this.getCustomerOrigin(data).pipe(switchMap(response => {
      if (response?.origin && socialNetworks.indexOf(response.origin.toLowerCase()) >= 0) {
        return throwError(response);
      }
      return this.customerService.resetPassword(data, this.keyClient);
    }));
  }

  simpleLogin(params: Login, provider: string, mergeCarts = true): Observable<any> {
    const idCustomerWebSafeAnonymous = this.customer?.idCustomerWebSafe;
    return this.customerService.loginPost(params, provider).pipe(
      tap(customer => {
        if (customer?.code === 'BUSINESS_ERROR' && customer?.message.includes('2FA')) {
          throw customer;
        } else {
          const dataForGetCustomer = {
            customerData: customer,
            deliveryType: this.locationManager.getDeliveryType(),
          }
          this.saveCustomer(customer);
          this.customerOnly(dataForGetCustomer).subscribe();
          this.gtmService.onLogin(customer);
        }
        return customer;
      }),
      catchError(error => throwError(error)),
      switchMap(customer => (mergeCarts ? this.mergeCarts(customer, idCustomerWebSafeAnonymous) : of(customer))),
    );
  }

  login(data): Observable<any> {
    const provider = data.registeredBy;

    const deliveryType = {deliveryType: data.deliveryType};

    if (data?.email) {
      data.email = data.email.toLowerCase();
    }

    if (data?.socialEmail) {
      data.socialEmail = data.socialEmail.toLowerCase();
    }

    const basicParams = {
      email: data.email || data.socialEmail,
    };

    const params: any = {
      keyClient: CustomerManager.getKeyClient(),
      latitude: CustomerManager.getCoordinates().lat,
      longitude: CustomerManager.getCoordinates().lon,
    };
    if (provider === 'EMAIL') {
      params.email = data.email;
      params.password = data.password;
      params.recaptchaPayload = data?.recaptchaPayload;
      delete params.keyClient;
      delete params.latitude;
      delete params.longitude;
    } else if (provider === 'APPLE') {
      params.email = data.email;
      params.tokenPlatform = data.token;
      params.uidFirebase = data.uidFirebase;
    } else {
      params.tokenPlatform = data.token;
      params.email = data.socialEmail || data.email;
    }

    return this.simpleLogin(params, provider)
      .pipe(
        first(),
        switchMap(response => this.get(deliveryType, response)),
        catchError(error => {
          if (error.code === 401 && error.message.toLowerCase().indexOf('contraseña') >= 0) {
            return throwError(error);
          }

          if (error.url?.includes('mergeShoppingCartAnonymous')
            || (error.code === 409 && error.message.toLowerCase().indexOf('carrito') >= 0)) {
            return this.simpleLogin(params, provider, false);
          }
          return this.customerService.getUserOrigin(basicParams)
            .pipe(
              switchMap(res => {
                if (res.statusCode === 200 && res?.code === 'NO_CONTENT') {
                  return throwError(error);
                }
                return throwError(res);
              }),
              catchError(() => throwError(error)),
            );
        }),
      );
  }

  mergeCarts(customer, idCustomerWebSafeAnonymous?) {

    const anonymousCustomer = this.customer;
    const sasCart = this.sasCartManager.getLocalCart();
    let obs = [];

    return this.cartManager.getAgile().pipe(switchMap(cart => {
      if (cart || sasCart) {
        if (cart) {
          const data = {
            idCustomerWebSafeAnonymous: idCustomerWebSafeAnonymous || anonymousCustomer.idCustomerWebSafe,
            token: customer.token?.token || customer.tokensDatastore?.token,
            tokenIdWebSafe: customer.token?.tokenIdWebSafe || customer.tokensDatastore?.tokenIdWebSafe,
            idCustomerWebSafe: customer.idCustomerWebSafe || customer.tokensDatastore?.idCustomerWebSafe,
          };
          obs = [
            this.cartManager.merge(data),
          ];
        }

        if (sasCart?.anonymousUuid) {
          const data = {
            customerId: customer.id,
            anonymousUuid: sasCart.anonymousUuid,
          };
          obs = [...obs, this.sasCartManager.merge(data)];
        }

        return zip(...obs).pipe(
          tap(() => {
            this.cartManager.deleteCartAction();
            this.sasCartManager.deleteCartAction();
          }),
          map(() => customer),
        );
      }
      this.cartManager.deleteCartAction();
      this.sasCartManager.deleteCartAction();
      return of(customer);
    }));
  }

  logOut(): Observable<any> {

    const params = {
      deviceId: navigator ? navigator.userAgent : '',
      firebaseTokenDevice: '',
      idCustomerWebSafe: this.customer.idCustomerWebSafe,
      isWeb: true,
    };
    this.gtmService.onLogOut();
    const query = (countryConfig.customerQuery) ? this.customerService.logOut(params) : of({});
    return query.pipe(finalize(() => {
      setTimeout(() => {
        localStorage.clear();
      }, 3000);
    }),
    tap(() => {
      this.lsm.removeAllItemsExcept(
        [
          'cities',
          'city',
          'originProperties',
          'enableSas',
          'programAndSaveDiscounts',
          'programAndSaveImages',
          'sasHourRange',
          'facets',
          'primeConfig'],
      );
      this.ssm.removeAllItemsExcept(['deliveryTypeText', 'departments', 'primeConfig']);

    }));
  }

  update(rawData): Observable<any> {
    const data: any = {
      id: this.customer.id,
      firstName: rawData.firstName,
      lastName: rawData.lastName,
      gender: rawData.gender,
      countryId: rawData.countryId,
      phone: `${rawData.countryCode}${rawData.phoneCode || ''}${rawData.phone}`,
      profileImageUrl: null,
      idCustomerWebSafe: this.customer.idCustomerWebSafe,
      token: {
        token: this.customer.token.token,
        tokenIdWebSafe: this.customer.token.tokenIdWebSafe,
      },
    };
    if (rawData.documentType) {
      data.documentType = rawData.documentType;
    }
    return this.customerService.update(data)
      .pipe(tap(res => {
        const newCustmerData = this.transformLocalData(res);
        const customer = { ...this.customer, ...newCustmerData };
        this.saveCustomer(customer);
      }));
  }

  createCustomerInterests(data, params): Observable<any> {
    return this.customerService.createCustomerInterests(data, params);
  }

  getOrigin(property): Observable<any> {
    return this.configurationManager.getOrigin(property);
  }

  getCustomerLogin(data): Observable<any> {
    this.removePotencialCustomer();
    return this.customerService.getUserOrigin(data);
  }

  sendMessage(data): Observable<any> {
    return this.customerService.sendMessage(data);
  }

  customerOnly(data): Observable<any> {
    const {idCustomerWebSafe, token, tokenIdWebSafe} = data.customerData?.tokensDatastore || {}  ;
    const {deliveryType} = data;
    const params = {
      deliveryType,
      idCustomerWebSafe,
      token,
      tokenIdWebSafe,
      latitude: CustomerManager.getCoordinates().lat,
      longitude: CustomerManager.getCoordinates().lon,
    };
    return this.customerService.get(params).pipe(
      delay(500),
      tap(res => {
        if (!res){ return; }
        if (res.analyticsUUID) {
          const data = {
            ...res,
            id: res.id || res.CustomerOldId,
            token: {idCustomerWebSafe, token, tokenIdWebSafe}
          }
          this.gtmService.onLogin(data);
        }
        const dataInfo = {
          ...res,
          previousItems: [],
          idCustomerWebSafe,
          isAnonymous: (res.CustomerOldId) <= 0,
          token: {token, tokenIdWebSafe},
          tokensJwt: data.customerData.tokensJwt
        }
        this.saveCustomer(dataInfo);
        this.sessionModalStatusEvent.changeModalStatus({close: true});
      }));
  }

  createPassword(data): Observable<any> {
    return this.customerService.createPassword(data);
  }

  getConfigurationModule(moduleName: string) {
    const module = this.ssm.get('multiCountry');
    return module ? module[moduleName] : {};
  }

  removePotencialCustomer() {
    this.ssm.removeItem('temporalCodeChangePass');
    this.ssm.removeItem('dataTemporal');
    this.ssm.removeItem('potencialCustomerExpress');
  }

  saveNonStockItem(data: SaveNonStockItem): Observable<SaveNonStockItemRes> {
    if (!data.email) {
      const customerEmail = this.customer?.email;
      if (customerEmail) {
       data.email = customerEmail;
      }
    }

    return this.customerService.saveNonStockItem(data).pipe(map(products => {
      if (products?.code === 'OK') {
        const itemsNonStock = this.lsm.get('nonStockItems') || [];
        itemsNonStock.push(data.itemId);
        this.lsm.set('nonStockItems', itemsNonStock);
      }
      return products;
    }));
  }

  getSaveNonStockItem(email: string): Observable<SaveNonStockItemRes> {
    return this.customerService.getSaveNonStockItem(email);
  }

  removeNonStockItem(data: SaveNonStockItem): Observable<SaveNonStockItemRes> {
    if (!data.email) {
      const customerEmail = this.customer?.email;
      if (customerEmail) {
       data.email = customerEmail;
      }
    }

    return this.customerService.removeNonStockItem(data).pipe(map(products => {
      const itemsNonStock = this.lsm.get('nonStockItems') || [];
      const removeItemNonStock = itemsNonStock.filter(item => item !== data.itemId);
      this.lsm.set('nonStockItems', removeItemNonStock);
      return products;
    }));
  }

  validateProductNonStock(idItem: number): Observable<boolean> {
    const itemsNonStock = this.lsm.get('nonStockItems') || [];
    const itemIncludeInNonStock = itemsNonStock.includes(idItem);
    return of(itemIncludeInNonStock);
  }

  verifyCustomer(): Observable<VerifyCustomerRes> {
    const customer = this.lsm.get('customer');

    if (!customer) {
      return throwError('No customer');
    }

    return this.customerService.verifyCustomer(customer?.id);
  }

  loginBmc(customer): Observable<LoginBmcResponse> {
    const {token, tokenIdWebSafe} = customer.token;
    const idCustomerWebSafe = customer.idCustomerWebSafe;
    return this.customerService.loginBmc({token, tokenIdWebSafe, idCustomerWebSafe});
  }

  getCustomerCreateData(customerData: CreatedCustomerRes): Observable<CreatedCustomerRes> {
    this.setLocalCustomer(customerData);
    return this.customerOnly({customerData}).pipe(
      map( (onlyCustomerData) => ({...onlyCustomerData,...customerData}))
    );
  }

  refreshTokensJwt(refreshToken: string): Observable<TokenJwtResponse> {
    return this.customerService.refreshTokensJwt(refreshToken).pipe(
      catchError(error => {
        this.lsm.removeItem('customer');
        this.lsm.set('refreshTokenJWT', true);
        if ( window.location.pathname !== '/') {
          window.location.href = '/';
        } else {
          window.location.reload();
        }
        return throwError(error);
      })
    );
  }

  private transformLocalData(customer: Customer) {
    const {tokensDatastore} = customer;
    const isAnonymous = !customer.customerId;
    if (tokensDatastore) {
      const {idCustomerWebSafe, token, tokenIdWebSafe} = tokensDatastore;
      const transformData = {
        ...customer,
        isAnonymous,
        id: customer.customerOldId,
        idCustomerWebSafe,
        token: {token, tokenIdWebSafe},
        idCustomerMulticountry: customer.customerId,
      }
      const anonymousData = {...this.defaultAnonymousData, ...transformData};
      return !isAnonymous ? transformData : anonymousData;
    }
    return customer;
  }

  setLocalCustomer(customer): void {
    const localCustomer = this.transformLocalData(customer);
    this.lsm.set('customer', localCustomer);
  }
}
